import React, { useEffect, useState } from 'react';
import AppShell from '../components/AppShell';
import api from '../lib/api';
import { errMsg } from '../lib/errors';
import { LoadingBlock } from '../components/States';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Save, RotateCcw, Building2, CreditCard, ShieldCheck, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../components/ui/badge';
import ImageUpload from '../components/ImageUpload';

export default function BusinessSettings() {
    const [s, setS] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try { const r = await api.get('/business-settings'); setS(r.data); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const upd = (path, val) => setS(cur => {
        const c = JSON.parse(JSON.stringify(cur));
        const keys = path.split('.'); let n = c;
        for (let i = 0; i < keys.length - 1; i++) { n[keys[i]] = n[keys[i]] || {}; n = n[keys[i]]; }
        n[keys[keys.length - 1]] = val; return c;
    });

    const save = async () => {
        setSaving(true);
        try {
            const { id, _id, created_at, updated_at, ...body } = s;
            await api.put('/business-settings', body);
            toast.success('Business settings saved'); load();
        } catch (e) { toast.error(errMsg(e, 'Save failed')); }
        finally { setSaving(false); }
    };
    const resetPolicies = async () => {
        if (!window.confirm('Reset all 4 policy texts (T&C, Privacy, Shipping, Refund) to defaults?')) return;
        try { await api.post('/business-settings/reset-policies'); toast.success('Policies reset'); load(); }
        catch (e) { toast.error(errMsg(e, 'Reset failed')); }
    };

    if (loading || !s) return <AppShell pageTitle="Business Settings"><LoadingBlock /></AppShell>;

    return (
        <AppShell pageTitle="Business Settings" pageActions={
            <div className="flex gap-2">
                <Button variant="outline" onClick={resetPolicies} data-testid="reset-policies-button"><RotateCcw className="h-4 w-4 mr-2" />Reset Policies</Button>
                <Button onClick={save} disabled={saving} data-testid="save-settings-button" style={{ background: 'hsl(142 71% 45%)', color: 'white' }}><Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save Changes'}</Button>
            </div>
        }>
            <div className="text-xs text-neutral-600 mb-3">These settings drive the storefront footer, contact page, invoice details, and GST calculation (business_state is used to decide CGST+SGST vs IGST).</div>
            <Tabs defaultValue="business">
                <TabsList className="mb-4">
                    <TabsTrigger value="business">Business</TabsTrigger>
                    <TabsTrigger value="contact">Contact & Social</TabsTrigger>
                    <TabsTrigger value="payments" data-testid="tab-payments">Payments</TabsTrigger>
                    <TabsTrigger value="seo">SEO</TabsTrigger>
                    <TabsTrigger value="policies">Policies</TabsTrigger>
                </TabsList>
                <TabsContent value="business">
                    <Card className="p-5 space-y-3">
                        <div className="text-sm font-semibold flex items-center gap-2"><Building2 className="h-4 w-4" />Company Profile</div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div><Label>Display Name</Label><Input value={s.name || ''} onChange={e => upd('name', e.target.value)} data-testid="biz-name" /></div>
                            <div><Label>Legal Name</Label><Input value={s.legal_name || ''} onChange={e => upd('legal_name', e.target.value)} /></div>
                            <div className="sm:col-span-2"><Label>Tagline</Label><Input value={s.tagline || ''} onChange={e => upd('tagline', e.target.value)} /></div>
                            <div><Label>Logo</Label><ImageUpload value={s.logo_url || ''} folder="business" testId="biz-logo" onChange={(url) => upd('logo_url', url)} /></div>
                            <div><Label>Favicon</Label><ImageUpload value={s.favicon_url || ''} folder="business" testId="biz-favicon" size="sm" onChange={(url) => upd('favicon_url', url)} /></div>
                            <div><Label>GSTIN</Label><Input value={s.gstin || ''} onChange={e => upd('gstin', e.target.value.toUpperCase())} placeholder="27ABCDE1234F1Z5" data-testid="biz-gstin" /></div>
                            <div><Label>PAN</Label><Input value={s.pan || ''} onChange={e => upd('pan', e.target.value.toUpperCase())} /></div>
                            <div><Label>CIN</Label><Input value={s.cin || ''} onChange={e => upd('cin', e.target.value)} /></div>
                            <div>
                                <Label>Business State (for GST) *</Label>
                                <Input value={s.business_state || ''} onChange={e => upd('business_state', e.target.value)} data-testid="biz-state" />
                                <div className="text-xs text-neutral-500 mt-1">Orders shipped to this state get CGST+SGST; others get IGST.</div>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3 border-t">
                            <div className="sm:col-span-2"><Label>Address Line 1</Label><Input value={s.address_line1 || ''} onChange={e => upd('address_line1', e.target.value)} data-testid="biz-line1" /></div>
                            <div className="sm:col-span-2"><Label>Address Line 2</Label><Input value={s.address_line2 || ''} onChange={e => upd('address_line2', e.target.value)} /></div>
                            <div><Label>City</Label><Input value={s.city || ''} onChange={e => upd('city', e.target.value)} /></div>
                            <div><Label>State</Label><Input value={s.state || ''} onChange={e => upd('state', e.target.value)} /></div>
                            <div><Label>Pincode</Label><Input value={s.pincode || ''} onChange={e => upd('pincode', e.target.value)} /></div>
                            <div><Label>Country</Label><Input value={s.country || ''} onChange={e => upd('country', e.target.value)} /></div>
                        </div>
                    </Card>
                </TabsContent>
                <TabsContent value="contact">
                    <Card className="p-5 space-y-3">
                        <div className="text-sm font-semibold">Contact</div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div><Label>Phone</Label><Input value={s.phone || ''} onChange={e => upd('phone', e.target.value)} data-testid="biz-phone" /></div>
                            <div><Label>WhatsApp</Label><Input value={s.whatsapp || ''} onChange={e => upd('whatsapp', e.target.value)} /></div>
                            <div><Label>Email</Label><Input value={s.email || ''} onChange={e => upd('email', e.target.value)} data-testid="biz-email" /></div>
                            <div><Label>Support hours</Label><Input value={s.support_hours || ''} onChange={e => upd('support_hours', e.target.value)} /></div>
                        </div>
                        <div className="text-sm font-semibold pt-3 border-t">Social Links</div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {['facebook', 'instagram', 'twitter', 'youtube', 'linkedin'].map(k => (
                                <div key={k}><Label className="capitalize">{k}</Label><Input value={(s.social || {})[k] || ''} onChange={e => upd(`social.${k}`, e.target.value)} placeholder="https://..." /></div>
                            ))}
                        </div>
                    </Card>
                </TabsContent>
                <TabsContent value="payments">
                    <PaymentsTab />
                </TabsContent>
                <TabsContent value="seo">
                    <Card className="p-5 space-y-3">
                        <div className="text-sm font-semibold">SEO Defaults</div>
                        <div><Label>Site Title</Label><Input value={(s.seo || {}).default_title || ''} onChange={e => upd('seo.default_title', e.target.value)} /></div>
                        <div><Label>Meta Description</Label><Textarea rows={3} value={(s.seo || {}).default_description || ''} onChange={e => upd('seo.default_description', e.target.value)} /></div>
                        <div><Label>Keywords</Label><Textarea rows={2} value={(s.seo || {}).default_keywords || ''} onChange={e => upd('seo.default_keywords', e.target.value)} /></div>
                    </Card>
                </TabsContent>
                <TabsContent value="policies">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {[
                            ['terms_and_conditions', 'Terms & Conditions'],
                            ['privacy_policy', 'Privacy Policy'],
                            ['shipping_policy', 'Shipping Policy'],
                            ['refund_policy', 'Refund Policy'],
                            ['about', 'About Us'],
                        ].map(([k, label]) => (
                            <Card key={k} className="p-5">
                                <Label className="text-sm font-semibold">{label}</Label>
                                <Textarea rows={12} className="font-mono text-xs" value={s[k] || ''} onChange={e => upd(k, e.target.value)} data-testid={`biz-${k}`} />
                                <div className="text-xs text-neutral-500 mt-1">Markdown supported. Displayed on storefront policy pages.</div>
                            </Card>
                        ))}
                    </div>
                </TabsContent>
            </Tabs>
        </AppShell>
    );
}


function PaymentsTab() {
    const [cfg, setCfg] = useState(null);
    const [webhook, setWebhook] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [s, w] = await Promise.all([
                api.get('/payments/razorpay/admin/settings'),
                api.get('/payments/razorpay/admin/webhook-status').catch(() => ({ data: null })),
            ]);
            setCfg(s.data);
            setWebhook(w.data);
        } catch (e) { toast.error(errMsg(e, 'Failed to load payment settings')); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const setMode = async (mode) => {
        if (!cfg) return;
        if (mode === cfg.razorpay_mode) return;
        // Warn strongly when switching to LIVE
        if (mode === 'live') {
            if (!window.confirm('Switch to LIVE mode?\n\nAll subsequent payments will charge REAL money on customer cards. Ensure your store is production-ready.')) return;
        }
        setSaving(true);
        try {
            await api.patch('/payments/razorpay/admin/settings', { razorpay_mode: mode });
            toast.success(`Razorpay mode set to ${mode.toUpperCase()}`);
            load();
        } catch (e) { toast.error(errMsg(e, 'Failed to update payment mode')); }
        finally { setSaving(false); }
    };

    if (loading || !cfg) return <LoadingBlock />;

    const activeConfigured = cfg.razorpay_mode === 'live' ? cfg.live?.configured : cfg.test?.configured;

    return (
        <div className="space-y-4">
            <Card className="p-5 space-y-4">
                <div className="flex items-center gap-2">
                    <CreditCard className="h-4 w-4 text-green-600" />
                    <div className="text-sm font-semibold">Razorpay Payment Gateway</div>
                    <Badge className={cfg.razorpay_mode === 'live' ? 'bg-red-100 text-red-800 hover:bg-red-100' : 'bg-amber-100 text-amber-800 hover:bg-amber-100'} data-testid="razorpay-active-mode">
                        {cfg.razorpay_mode?.toUpperCase()} MODE
                    </Badge>
                </div>
                <div className="text-xs text-neutral-600">
                    Choose the mode for online payments. TEST mode uses Razorpay&apos;s sandbox and does not charge real money.
                    LIVE mode processes real transactions on customer cards/UPI.
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {/* TEST card */}
                    <button
                        type="button"
                        onClick={() => setMode('test')}
                        disabled={saving}
                        data-testid="razorpay-set-test"
                        className={`text-left rounded-lg border-2 p-4 transition-colors ${cfg.razorpay_mode === 'test' ? 'border-amber-500 bg-amber-50' : 'border-neutral-200 hover:border-neutral-300 bg-white'}`}
                    >
                        <div className="flex items-center justify-between">
                            <div className="text-sm font-semibold">Test Mode</div>
                            <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Sandbox</Badge>
                        </div>
                        <div className="mt-2 text-xs text-neutral-600 space-y-1">
                            <div className="flex items-center gap-1">
                                {cfg.test?.configured
                                    ? <><ShieldCheck className="h-3.5 w-3.5 text-green-600" /> Keys configured</>
                                    : <><AlertCircle className="h-3.5 w-3.5 text-red-500" /> Test keys not set in server .env</>}
                            </div>
                            {cfg.test?.configured && <div className="font-mono text-neutral-500">{cfg.test.key_id}</div>}
                        </div>
                    </button>

                    {/* LIVE card */}
                    <button
                        type="button"
                        onClick={() => setMode('live')}
                        disabled={saving}
                        data-testid="razorpay-set-live"
                        className={`text-left rounded-lg border-2 p-4 transition-colors ${cfg.razorpay_mode === 'live' ? 'border-red-500 bg-red-50' : 'border-neutral-200 hover:border-neutral-300 bg-white'}`}
                    >
                        <div className="flex items-center justify-between">
                            <div className="text-sm font-semibold">Live Mode</div>
                            <Badge className="bg-red-100 text-red-800 hover:bg-red-100">Production</Badge>
                        </div>
                        <div className="mt-2 text-xs text-neutral-600 space-y-1">
                            <div className="flex items-center gap-1">
                                {cfg.live?.configured
                                    ? <><ShieldCheck className="h-3.5 w-3.5 text-green-600" /> Keys configured</>
                                    : <><AlertCircle className="h-3.5 w-3.5 text-red-500" /> Live keys not set in server .env</>}
                            </div>
                            {cfg.live?.configured && <div className="font-mono text-neutral-500">{cfg.live.key_id_masked}</div>}
                        </div>
                    </button>
                </div>

                {!activeConfigured && (
                    <div className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-800 flex items-start gap-2">
                        <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                        <div>
                            <div className="font-semibold">Active mode ({cfg.razorpay_mode.toUpperCase()}) is not fully configured.</div>
                            <div>Ask the system administrator to add {cfg.razorpay_mode === 'live' ? 'RAZORPAY_LIVE_KEY_ID / RAZORPAY_LIVE_KEY_SECRET' : 'RAZORPAY_TEST_KEY_ID / RAZORPAY_TEST_KEY_SECRET'} to the server environment (.env).</div>
                        </div>
                    </div>
                )}

                <div className="rounded-md border bg-neutral-50 p-3 text-xs text-neutral-700 space-y-1">
                    <div className="font-semibold text-neutral-800">Security notes</div>
                    <ul className="list-disc pl-5 space-y-0.5">
                        <li>Secret keys are stored only in server environment variables — never in the database or frontend.</li>
                        <li>Only the active mode&apos;s public Key ID is sent to the checkout page.</li>
                        <li>All payments are verified server-side using Razorpay&apos;s signature check before an order is marked paid.</li>
                        <li>Switching modes takes effect immediately for new checkout sessions.</li>
                    </ul>
                </div>
            </Card>

            {/* Webhook card */}
            <Card className="p-5 space-y-3">
                <div className="flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4 text-green-600" />
                    <div className="text-sm font-semibold">Webhook (async payment confirmation)</div>
                    <Badge className={webhook?.configured ? 'bg-green-100 text-green-800 hover:bg-green-100' : 'bg-neutral-200 text-neutral-800 hover:bg-neutral-200'} data-testid="razorpay-webhook-status">
                        {webhook?.configured ? 'CONFIGURED' : 'NOT CONFIGURED'}
                    </Badge>
                </div>
                <div className="text-xs text-neutral-600">
                    Webhooks let Razorpay confirm payments to the server even if the customer&apos;s browser closes before the redirect.
                    This dramatically improves reliability — highly recommended for LIVE mode.
                </div>

                <div className="space-y-2">
                    <div>
                        <div className="text-[11px] font-medium text-neutral-600 mb-1">1. Webhook URL (paste this into Razorpay Dashboard)</div>
                        <div className="flex items-center gap-2">
                            <code className="flex-1 text-xs bg-neutral-100 rounded px-2 py-1.5 truncate" data-testid="razorpay-webhook-url">
                                {`${window.location.origin}${webhook?.path || '/api/payments/razorpay/webhook'}`}
                            </code>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                    navigator.clipboard.writeText(`${window.location.origin}${webhook?.path || '/api/payments/razorpay/webhook'}`);
                                    toast.success('Webhook URL copied');
                                }}
                                data-testid="copy-webhook-url"
                            >Copy</Button>
                        </div>
                    </div>
                    <div>
                        <div className="text-[11px] font-medium text-neutral-600 mb-1">2. Active events to subscribe</div>
                        <div className="flex flex-wrap gap-1.5">
                            {(webhook?.recommended_events || ['payment.captured', 'payment.failed', 'order.paid']).map(ev => (
                                <Badge key={ev} className="bg-neutral-100 text-neutral-800 hover:bg-neutral-100 font-mono text-[11px]">{ev}</Badge>
                            ))}
                        </div>
                    </div>
                    <div>
                        <div className="text-[11px] font-medium text-neutral-600 mb-1">3. Webhook secret</div>
                        <div className="text-xs text-neutral-600">
                            Generate a strong secret in Razorpay Dashboard when creating the webhook, then set
                            <code className="mx-1 bg-neutral-100 rounded px-1 py-0.5">RAZORPAY_WEBHOOK_SECRET</code>
                            in the server <code className="bg-neutral-100 rounded px-1 py-0.5">.env</code> file and restart the backend.
                        </div>
                    </div>
                </div>

                {!webhook?.configured && (
                    <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 flex items-start gap-2">
                        <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                        <div>
                            <div className="font-semibold">Webhook is not configured</div>
                            <div>Payments will still work via the client-side verification flow. Enable webhooks for extra reliability against browser drops and network failures.</div>
                        </div>
                    </div>
                )}

                {webhook?.configured && (webhook?.recent_events?.length ?? 0) > 0 && (
                    <div className="pt-2">
                        <div className="text-[11px] font-medium text-neutral-600 mb-1">Recent webhook events</div>
                        <div className="rounded-md border divide-y">
                            {webhook.recent_events.slice(0, 6).map(ev => (
                                <div key={ev.id} className="px-2 py-1.5 text-[11px] flex items-center gap-2">
                                    <Badge className={ev.event?.includes('failed') ? 'bg-red-100 text-red-800 hover:bg-red-100' : 'bg-green-100 text-green-800 hover:bg-green-100'}>
                                        {ev.event}
                                    </Badge>
                                    <span className="font-mono text-neutral-500 truncate">{ev.razorpay_payment_id || ev.razorpay_order_id || '-'}</span>
                                    <span className="ml-auto text-neutral-500">{new Date(ev.received_at).toLocaleString()}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </Card>
        </div>
    );
}
